import { FunctionComponent, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { dataURLToBlob } from 'blob-util'
import classNames from 'classnames'
import type { View } from 'vega'
import { downloadBlob } from '../../../misc/blob.utilities'
import { RoundedButton } from '../../shared'
import { getChartDimensions, prepareVegaSpec } from './prepareVegaSpec'
import { VegaChartProps } from './VegaChart.model'
import styles from './VegaChart.module.scss'

/** Largeur du conteneur arrondie à 10 px : évite de re-rendre le graphique à chaque pixel de redimensionnement. */
const useContainerWidth = (ref: RefObject<HTMLElement>): number => {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(Math.round(entry.contentRect.width / 10) * 10)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return width
}

export const VegaChart: FunctionComponent<VegaChartProps> = ({
  vegaSpec,
  title,
  compact = false,
  onExpand,
}) => {
  const hostRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<View | null>(null)
  const containerWidth = useContainerWidth(hostRef)
  const [isLoading, setIsLoading] = useState(true)
  const [hasRenderError, setHasRenderError] = useState(false)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host || containerWidth === 0) return

    let cancelled = false
    const { width, height } = getChartDimensions(containerWidth, compact)
    setIsLoading(true)
    setHasRenderError(false)

    import('vega-embed')
      .then(({ default: embed }) =>
        embed(host, prepareVegaSpec(vegaSpec, width, height), { actions: false, renderer: 'svg' }),
      )
      .then(({ view }) => {
        if (cancelled) {
          view.finalize()
          return
        }
        viewRef.current = view
        setIsLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setIsLoading(false)
        setHasRenderError(true)
      })

    return () => {
      cancelled = true
      viewRef.current?.finalize()
      viewRef.current = null
    }
  }, [compact, containerWidth, vegaSpec])

  const handleExportPng = useCallback(async () => {
    if (!viewRef.current) return
    setExporting(true)
    try {
      const url = await viewRef.current.toImageURL('png')
      downloadBlob(dataURLToBlob(url), `${title.replace(/\s+/g, '_').slice(0, 60)}.png`)
    } finally {
      setExporting(false)
    }
  }, [title])

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div className={styles.headerActions}>
          {compact && onExpand && (
            <RoundedButton
              label="Agrandir"
              icon="externalLink"
              theme="transparent-dark"
              size="nano"
              onClick={onExpand}
              testId="button-stats_assistant-chart-expand"
            />
          )}
          <RoundedButton
            label="Export PNG"
            icon="export"
            theme="transparent-dark"
            size="nano"
            disabled={isLoading || exporting || hasRenderError}
            onClick={handleExportPng}
            testId="button-stats_assistant-chart-export_png"
          />
        </div>
      </div>
      {isLoading && !hasRenderError && <div className={styles.skeleton} />}
      {hasRenderError && (
        <div className={styles.renderError}>Impossible d’afficher le graphique.</div>
      )}
      <div
        ref={hostRef}
        className={classNames(styles.chartHost, compact ? styles.compact : styles.expanded)}
      />
    </div>
  )
}
